import React, { useMemo, useCallback } from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  Autocomplete,
  TextField,
  FormHelperText,
  InputAdornment,
  Chip,
  ListItem,
  Typography,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { FnComponents, I18n } from "utilities/utilities";
import { I18NextNs, TypeComponent } from "enum/enum";
import { PopperCustom, checkedIcon, icon } from ".";
import { IMultiSelectItemProps } from "./SelectProps";

export const MultipleSelectItem = (props: IMultiSelectItemProps) => {
  const {
    id,
    name,
    label,
    fullWidth = true,
    limitTag = 2,
    defaultValue = [],
    IsSelectAllOption = true,
    IsClearable = false,
    disabled = false,
    IsShowMessageError = true,
    IsPopperCustom = true,
    IsShowCountSelected = false,
    required = false,
  } = props;

  const { control } = useFormContext();

  const rules = useMemo(() => {
    return {
      required: {
        value: required,
        message: `${I18n.SetText("required", I18NextNs.validation)} ${label}`,
      },
    };
  }, [required, label]);

  const getOpObjValue = (val) => {
    let res = [];
    if (val) {
      val.forEach((element) => {
        res.push(props.options.find((op) => op.value === element));
      });
    }
    return res;
  };

  const onHandleRenderOption = useCallback(

    (props_, option, value, selected) => {
      if (props.options && props.options.length > 0) {
        return (
          <ListItem {...props_} key={option.value}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={
                option.value === "SelectAll"
                  ? (value || []).length === (props.options || []).length
                  : selected
              }
              disabled={option.disabled}
            />
            {option.label}
          </ListItem>
        );
      } else {
        return (
          <Typography sx={{ display: "flex", justifyContent: "center" }}>
            {I18n.SetText("noInformationFound")}
          </Typography>
        );
      }
    },
    [props.options]
  );

  const onFilterOption = useCallback(
    (options, params) => {
      const filtered = params.inputValue ? options.filter((f: any) => f.label.toLowerCase().includes(params.inputValue.toLowerCase())) : options;
      if (props.limits != null) {
        let filtered_ = filtered.slice(0, props.limits);
        return IsSelectAllOption ? [{ label: I18n.SetText("itemSelectedAll"), value: "SelectAll" }, ...filtered_] : filtered_;
      } else {
        let obj = { label: I18n.SetText("itemSelectedAll"), value: "SelectAll"}
        //  if ((I18n.SetText("itemSelectedAll")).includes(params.inputValue.toLowerCase())) {
        //   obj = { label: I18n.SetText("itemSelectedAll"), value: "SelectAll" }
        //  }

        return IsSelectAllOption && filtered.length > 0 ? [obj, ...filtered,] : filtered;
      }
    },
    [IsSelectAllOption, props.limits]
  );

  const onRenderTags = useCallback(
    (tagValue, getTagProps) => {
      return (
        <>
          {tagValue.slice(0, limitTag).map((option: any, index) => (
            <Chip
              key={option?.value}
              size={"small"}
              {...getTagProps({ index })}
              label={option?.label}
              disabled={option?.disabled || disabled || false}
            />
          ))}
          {tagValue.length > limitTag ? (
            <Chip
              size={"small"}
              label={"+" + (tagValue.length - limitTag)}
              disabled={disabled || false}
            />
          ) : (
            <></>
          )}
        </>
      );
    },
    [disabled, limitTag]
  );

  const onHandleOnchange = useCallback(
    (event, selectedOptions, reason, value, onChangeController) => {
      let valueOnchange = [];
      if (reason === "selectOption" || reason === "removeOption") {
        selectedOptions = selectedOptions.filter(
          (f) => f !== undefined && f != null
        );
        //Select All
        if (selectedOptions.find((option) => option.value === "SelectAll")) {
          const IsAllSelected = props.options.length === (value || []).length;
          if (!IsAllSelected) {
            onChangeController(
              props.options
                .filter((f) => f.value !== "SelectAll")
                .map((m) => m.value)
            );
            valueOnchange = props.options
              .filter((f) => f.value !== "SelectAll")
              .map((m) => m.value);
          } else {
            onChangeController([]);
            valueOnchange = [];
          }
        } else {
          let arr = (selectedOptions || [])
            .filter((f) => f.value !== "SelectAll")
            .map((m) => m["value"]);

          onChangeController(arr);
          // valueOnchange = selectedOptions.filter((f) => f.value !== "SelectAll")
          valueOnchange = arr;
        }
      } else if (reason === "clear") {
        onChangeController([]);
        valueOnchange = [];
      }

      if (props.onChange) {
        props.onChange(valueOnchange, event);
      }
    },
    [props]
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      shouldUnregister={true}
      defaultValue={defaultValue}
      render={({
        field: { onChange, onBlur, value, ref },
        fieldState: { invalid, error },
      }) => {
        return (
          <React.Fragment>
            <Autocomplete
              multiple
              disableCloseOnSelect
              ref={ref}
              id={FnComponents.GetId(TypeComponent.select, id, name)}
              disabled={disabled}
              fullWidth={fullWidth}
              size={"small"}
              options={props.options}
              value={getOpObjValue(value)}
              openText={I18n.SetText("autoCompleteOpenText")}
              closeText={I18n.SetText("autoCompleteCloseText")}
              noOptionsText={
                props.notOptionsText || I18n.SetText("noInformationFound")
              }
              disableClearable={IsClearable}
              PopperComponent={IsPopperCustom ? PopperCustom : undefined}
              getOptionDisabled={(option) =>
                option.disabled
              }
              renderOption={(props_, option, { selected }, params) => {

                return onHandleRenderOption(props_, option, value, selected);
              }}
              getOptionLabel={(itemOption: any) => {
                return `${itemOption?.label}`;
              }}
              slotProps={{
                popper: {
                  sx: {
                    zIndex: 2000
                  }
                }
              }}
              filterOptions={onFilterOption}
              renderInput={(params) => (
                <TextField
                  id={FnComponents.GetId(TypeComponent.input, id, name)}
                  {...params}
                  name={name}
                  error={invalid}
                  required={props.required}
                  disabled={disabled}
                  label={label}
                  sx={{ paddingRight: "0px !important" }}
                  placeholder={
                    getOpObjValue(value).length === 0 ? props.placeholder : null
                  }
                  size={"small"}
                  fullWidth={fullWidth}
                  style={props.style}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: props.startAdornment ? (
                      <>
                        <InputAdornment position="start">
                          {props.startAdornment}
                        </InputAdornment>
                        params.InputProps.startAdornment
                      </>
                    ) : (
                      params.InputProps.startAdornment
                    ),
                    endAdornment: (
                      <>
                        {IsShowCountSelected
                          ? value.length + " " + I18n.SetText("itemSelected")
                          : params.InputProps.endAdornment}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={onRenderTags}
              onChange={(event, selectedOptions, reason) => {
                onHandleOnchange(
                  event,
                  selectedOptions,
                  reason,
                  value,
                  onChange
                );
              }}
              onBlur={(event) => {
                onBlur();
                if (props.onBlur) {
                  props.onBlur(event);
                }
              }}
              onKeyDown={(event) => {
                if (props.onKeyDown) {
                  props.onKeyDown(event);
                }
              }}
              onKeyUp={(event) => {
                if (props.onKeyUp) {
                  props.onKeyUp(event);
                }
              }}
            />
            {IsShowMessageError && error ? (
              <FormHelperText>{error.message}</FormHelperText>
            ) : null}
          </React.Fragment>
        );
      }}
    />
  );
};
